import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import PhoneIcon from "assets/icons/phoneIcon.svg";
import CustomDialog from "Components/shared/CustomDialog";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveIssue } from "services/dashboard";
import { useState } from "react";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import { useRTL } from "hooks/isRTL";

/**
 * Renders the footer of a flashing card, including a driver contact dialog, a "Mark as Resolved" button,
 * and a confirmation dialog for resolving issues. Also displays success or error messages via a Snackbar.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.dialogOpen - Indicates if the driver contact dialog is open.
 * @param {Function} props.handleDialogOpen - Function to open the driver contact dialog.
 * @param {Function} props.handleDialogClose - Function to close the driver contact dialog.
 * @param {string} props.driverPhoneNumber - The phone number of the driver.
 * @param {string} props.senderPhoneNumber - The phone number of the sender.
 * @param {string} props.receiverPhoneNumber - The phone number of the receiver.
 * @param {string} props.issueId - The unique identifier for the issue being resolved.
 *
 * @returns {JSX.Element} The rendered component.
 */
function FlashingCardFooter({
  dialogOpen,
  handleDialogOpen,
  handleDialogClose,

  driverPhoneNumber,
  senderPhoneNumber,
  receiverPhoneNumber,
  issueId,
}) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [snackbar, setSnackbar] = useState({
    message: "",
    severity: "success",
  });
  const { t } = useRTL();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) => resolveIssue(payload),
    onSuccess: () => {
      setConfirmDialogOpen(false);
      setReason("");
      setSnackbar({
        message: t("issueResolvedSuccessfully"),
        severity: "success",
      });
      queryClient.invalidateQueries(["issues"]);
    },
    onError: (error) => {
      console.error("Error resolving issue:", error);
      setConfirmDialogOpen(false);
      setSnackbar({
        message: t("errorResolvingIssue"),
        severity: "error",
      });
    },
  });

  const handleResolveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleApprove = () => {
    if (!reason.trim()) {
      setSnackbar({
        message: t("resolutionNoteRequired"),
        severity: "error",
      });
      return;
    }
    mutation.mutate({ issueId, resolveNote: reason });
  };

  const handleDecline = () => {
    setReason("");
    setConfirmDialogOpen(false);
  };
  const handleSnackbarClose = () => {
    setSnackbar({ message: "", severity: "success" });
  };

  return (
    <Box sx={{ display: "flex", marginTop: "0.5rem", gap: "1rem" }}>
      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        content={[
          { label: t("driverPhoneNumber"), value: driverPhoneNumber },
          { label: t("senderPhoneNumber"), value: senderPhoneNumber },
          { label: t("receiverPhoneNumber"), value: receiverPhoneNumber },
        ]}
        title={t("contactInformation")}
      />
      <Box
        onClick={handleDialogOpen}
        sx={{
          width: "35px",
          height: "35px",
          backgroundColor: "#ABABAB96",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        <img
          src={PhoneIcon}
          alt="Phone"
          style={{ width: "28px", height: "28px" }}
        />
      </Box>

      <Button
        variant="contained"
        sx={{
          backgroundColor: "text.black",
          color: "text.white",
          fontWeight: 800,
          flex: "1",
        }}
        onClick={handleResolveClick}
        disabled={mutation.isLoading}
      >
        {mutation.isLoading ? t("resolving...") : t("markAsResolved")}
      </Button>
      {/* di */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleDecline}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "600px",
            minHeight: "200px",
          },
        }}
      >
        <DialogTitle>{t("confirmResolution")}</DialogTitle>
        <DialogContent>
          <TextField
            placeholder={t("enterResolutionReason")}
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDecline} color="secondary">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleApprove}
            color="primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? t("resolving...") : t("submit")}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomSnackbar
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
}

export default FlashingCardFooter;
